<template>
  <div class="scripts-page">
    <div class="scripts-header">
      <div class="title">
        <h1 class="text-xl font-semibold">
          {{ $t('hit-admin.common.scripts') }}
        </h1>
      </div>

      <div class="controls">
        <hit-select
          class="min-w-[32rem] w-full"
          :label="'Sélectionner un script'"
          :options="scriptOptions"
          :value="selectedScriptId"
          item-id-property="id"
          :label-fields="['label']"
          clearable
          @change="onScriptChange"
        />
      </div>
    </div>

    <div class="scripts-body">
      <div v-if="!selectedScriptId" class="empty-state">
        <div class="empty-card">
          <div class="empty-title">Aucun script sélectionné</div>
          <div class="empty-subtitle">
            Sélectionnez un script dans la liste ci-dessus.
          </div>
        </div>
      </div>

      <script-update-company-status
        v-else-if="selectedScriptId === 'update_company_status'"
      />

      <script-copy-base-data
        v-else-if="selectedScriptId === 'copy_base_data'"
      />

      <script-deactivate-company
        v-else-if="selectedScriptId === 'deactivate_hit_online_company'"
      />

      <script-block-company v-else-if="selectedScriptId === 'block_company'" />

      <script-manage-api-keys
        v-else-if="selectedScriptId === 'manage_api_keys'"
      />

      <script-force-refresh-webui
        v-else-if="selectedScriptId === 'force_refresh_webui'"
      />

      <script-compute-usage v-else-if="selectedScriptId === 'compute_usage'" />

      <div v-else class="empty-state">
        <div class="empty-card">
          <div class="empty-title">Script non supporté</div>
          <div class="empty-subtitle">
            Ce script n’a pas encore d’interface dans l’admin UI.
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {defineComponent} from 'vue';
import {HitSelect} from '@hit/components';
import {adminApi} from '@admin/services/adminApi';
import ScriptUpdateCompanyStatus from './ScriptUpdateCompanyStatus.vue';
import ScriptCopyBaseData from './ScriptCopyBaseData.vue';
import ScriptDeactivateCompany from './ScriptDeactivateCompany.vue';
import ScriptBlockCompany from './ScriptBlockCompany.vue';
import ScriptManageApiKeys from './ScriptManageApiKeys.vue';
import ScriptForceRefreshWebui from './ScriptForceRefreshWebui.vue';
import ScriptComputeUsage from './ScriptComputeUsage.vue';

export default defineComponent({
  name: 'ScriptsPage',
  components: {
    HitSelect,
    ScriptUpdateCompanyStatus,
    ScriptCopyBaseData,
    ScriptDeactivateCompany,
    ScriptBlockCompany,
    ScriptManageApiKeys,
    ScriptForceRefreshWebui,
    ScriptComputeUsage,
  },
  data() {
    return {
      scripts: [],
      selectedScriptId: null,
    };
  },
  computed: {
    scriptOptions() {
      return (this.scripts || []).map((s) => ({
        id: s.id,
        label: s.label,
        description: s.description,
        runEndpoint: s.runEndpoint,
        params: s.params || {},
      }));
    },
  },
  async mounted() {
    await this.loadScripts();
  },
  methods: {
    async loadScripts() {
      try {
        const scripts = await adminApi.listScripts();
        this.scripts = scripts || [];
      } catch (e) {
        this.scripts = [];
        // do not surface error in page-level result, children can handle their own state
      }
    },
    onScriptChange(value) {
      this.selectedScriptId = value;
    },
  },
});
</script>

<style lang="scss" scoped>
.scripts-page {
  padding: 0.75rem;
  height: 100%;
  display: grid;
  grid-template-rows: max-content minmax(0, 1fr);
  gap: 0.75rem;
}

.scripts-header {
  display: grid;
  gap: 0.5rem;
  padding: 0.5rem 0.65rem 0.65rem;
  border-bottom: 1px solid rgba(15, 23, 42, 0.08);
  background: rgba(255, 255, 255, 0.72);
  backdrop-filter: blur(6px);
  border-radius: 0.75rem;
}

.controls {
  display: flex;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 0.85rem;
}

.scripts-body {
  min-height: 0;
}

.empty-state {
  height: 100%;
  min-height: 0;
  display: grid;
  place-items: start center;
  padding: 1.25rem 0.75rem 1.5rem;
  padding-top: 2rem;
}

.empty-card {
  width: min(720px, 100%);
  border-radius: 1rem;
  border: 1px solid rgba(15, 23, 42, 0.08);
  background: rgba(255, 255, 255, 0.82);
  backdrop-filter: blur(6px);
  box-shadow:
    0 1px 1px rgba(16, 24, 40, 0.04),
    0 12px 30px rgba(16, 24, 40, 0.08);
  padding: 1.25rem 1.25rem;
}

.empty-title {
  font-weight: 700;
  font-size: 1.05rem;
  color: rgba(15, 23, 42, 0.95);
}

.empty-subtitle {
  margin-top: 0.35rem;
  color: rgba(100, 116, 139, 1);
  font-size: 0.9rem;
  line-height: 1.35rem;
}

.result-title {
  font-weight: 700;
}
.result-title.ok {
  color: #03695a;
}
.result-title.ko {
  color: #b91c1c;
}
.result-pre {
  margin-top: 0.5rem;
  font-size: 0.75rem;
  line-height: 1.15rem;
  background: rgba(248, 250, 252, 1);
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 0.65rem;
  padding: 0.6rem 0.65rem;
  max-height: 18rem;
  overflow: auto;
  white-space: pre-wrap;
}
</style>
